import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import Spotlights from 'components/Spotlights';
import Grain from 'components/UI/Grain';
import LoaderContent from 'components/UI/LoaderContent';
import useTheme from 'helpers/useTheme';
import getStyles from 'helpers/getStyles';

import { CardTypeOptions, ECollectionLayoutOptions, UserObjectType } from 'constants/graphqlTypes';
import { useGetSpotlights } from 'graphQL/spotlights/hook';
import { COLORS } from 'styles/constants';
import { useNavigationRef } from 'components/Navigation/helpers/hooks';
import { useProfileContext } from 'helpers/useProfileProvider';

import ListGrid from 'components/List/Grid';
import usePageType from 'helpers/usePageType';
import { useGetUserCards } from 'graphQL/cards/userCards/hooks';
import Info from './Info';
import { StyledSection } from './styled';

interface IProfileTopProps {
  userData?: UserObjectType;
  loading: boolean;
  setHideSpotlights: Dispatch<SetStateAction<boolean>>;
  hideSpotlights: boolean;
}

const ProfileTop: FC<IProfileTopProps> = ({ userData, loading, setHideSpotlights, hideSpotlights }) => {
  const [index, setIndex] = useState(0);
  const [hiddenCards, setHiddenCards] = useState<string[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const spotlightsData = useGetSpotlights();
  const isDark = useTheme();
  const { isMyProfile } = usePageType();
  const { cards, loading: spotlightsLoading, listsCount } = spotlightsData;
  const { gradientStart, gradientEnd } = getStyles(cards[index]?.cardStyle);
  const [hideGradient, setHideGradient] = useState(false);
  const { setGradientStart, setGradientEnd } = useProfileContext();
  const { userInfo, userStyle } = userData ?? {};
  const type = userStyle?.layoutCollection === ECollectionLayoutOptions.ImageGrid ? CardTypeOptions.Card : undefined;
  const { recentCards, loadMore, totalCount, loading: loadingItems } = useGetUserCards({
    type,
    includeQuestionResponse: true,
  });
  const { avatarColorGradientStart, avatarColorGradientEnd } = userInfo ?? {};

  const isCurrentSpotlightHidden = hiddenCards.includes(cards[index]?.id);

  const bothReady = totalCount !== 0 && listsCount !== 0;

  useEffect(() => {
    if (gradientEnd && gradientStart) {
      setGradientEnd?.(gradientEnd);
      setGradientStart?.(gradientStart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradientStart, gradientEnd]);

  const fontColor = isDark || !hideGradient ? COLORS.white[100] : COLORS.brown.dark[100];
  const spotlightsChangeKey = JSON.stringify(cards.map(({ id }) => id));
  const ref = useNavigationRef(fontColor);

  useEffect(() => {
    setIndex(0);
  }, [spotlightsChangeKey]);

  useEffect(() => {
    setHiddenCards(cards.filter(({ isHidden }) => !!isHidden).map(({ id }) => id));
  }, [cards, cards.length]);

  useEffect(() => {
    setHideSpotlights(spotlightsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlightsLoading]);

  useEffect(() => {
    setHideGradient(!cards.length || isCurrentSpotlightHidden || spotlightsLoading || loading);
  }, [isCurrentSpotlightHidden, cards.length, spotlightsLoading, loading]);

  const handleTabChange = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
  };

  const renderContent = () => {
    if (loading) {
      return <LoaderContent isCentered isDark={isDark} isFullScreen />;
    }

    return (
      <>
        <Info
          isDark={isDark}
          showGradient={activeTabIndex === 1}
          userData={userData}
          fontColor={fontColor}
          noContent={!cards.length}
          hideGradient={hideGradient}
          onTabChange={handleTabChange}
          cardsCount={bothReady ? totalCount : 0}
          listsCount={bothReady ? listsCount : 0}
          gradientStart={activeTabIndex === 1 ? avatarColorGradientStart : null}
          gradientEnd={activeTabIndex === 1 ? avatarColorGradientEnd : null}
          showStyledGrain={activeTabIndex === 1}
        />

        {activeTabIndex === 0 && userData && (
          <>
            <ListGrid
              hideActionsInfo={isMyProfile}
              layout={ECollectionLayoutOptions.Default}
              items={recentCards}
              loadMore={loadMore}
              loading={loadingItems}
              isDarkFromProps={isDark}
              isProfilePicks
            />
          </>
        )}

        {activeTabIndex === 1 && !hideSpotlights && (
          <Spotlights
            hiddenCards={hiddenCards}
            setHiddenCards={setHiddenCards}
            spotlightsData={spotlightsData}
            fontColor={fontColor}
            setIndex={setIndex}
            key={spotlightsChangeKey}
          />
        )}
      </>
    );
  };

  return (
    <StyledSection
      ref={cards.length ? ref : null}
      fontColor={fontColor}
      fixedHeight={!!cards.length}
      isDark={isDark}
      gradientStart={activeTabIndex === 0 ? gradientStart ?? avatarColorGradientStart ?? undefined : undefined}
      gradientEnd={activeTabIndex === 0 ? gradientEnd ?? avatarColorGradientEnd ?? undefined : undefined}
    >
      {activeTabIndex === 0 && userData && <Grain />}
      {renderContent()}
    </StyledSection>
  );
};

export default ProfileTop;
